<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'jobs',
    path: '/jobs',
    component: 'Jobs',
    title: () => gettext('Jobs'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Jobs',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'number',
      name: $fn.value.pgettext('Jobs', 'Number'),
      type: 'command',
      formatter: [
        {
          name: 'template',
          format: '#{{ number }}',
        },
        {
          name: 'link',
          href: '#',
        },
      ],
    },
    {
      field: 'status',
      name: $fn.value.pgettext('Jobs', 'Status'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'gettext',
            context: 'JobStatus',
          },
          {
            name: 'cellclass',
            options: {
              Running: 'bg-warning-light',
              'Errored/Rerunning': 'bg-warning-light',
              Errored: 'bg-danger-light',
              Failed: 'bg-danger-light',
              Completed: 'bg-success-light',
            },
            optionsSource: 'fieldValue',
          },
        ],
      },
    },
    {
      field: 'job_type',
      name: $fn.value.pgettext('Jobs', 'Job type'),
    },
    {
      field: 'created_by',
      name: $fn.value.pgettext('Jobs', 'User'),
      formatter: {
        name: 'user',
      },
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('Jobs', 'Submit time'),
      formatter: {
        name: 'datetime',
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('browse_jobs');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Jobs'), icon: $config.value.jobs.icon };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_zii4ipawv_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_i13ngphq2_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_bn5pz6qsn_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'DunningLetter',
      props: {
        id: $event.data.data.job_data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_pih1np55r_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'job_data';
  };
  const act_2248226175642056_showModal_az0t9zsqm_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'JobModal',
      props: {
        id: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_bt5clc4rm_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'number';
  };

  const act_7315092382398562_closeModal_zii4ipawv =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_zii4ipawv_getActionArgs,
    });
  const inst_evh_7315092382398562_close_61u8jwiuu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_zii4ipawv,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_61u8jwiuu],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_k6b0pui7a =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_7eoge1nta =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_k6b0pui7a,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_7eoge1nta],
    event: 'reload',
  });
  const act_2248226175642056_setData_i13ngphq2 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_i13ngphq2_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_aeopa0ra0 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_i13ngphq2,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_aeopa0ra0],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_bn5pz6qsn =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_bn5pz6qsn_getActionArgs,
    });
  const inst_evh_2248226175642056_command_pih1np55r =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_bn5pz6qsn,
      event: 'command',
      when: inst_evh_2248226175642056_command_pih1np55r_getWhen,
      displayName: 'showModal',
    });
  const act_2248226175642056_showModal_az0t9zsqm =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_az0t9zsqm_getActionArgs,
    });
  const inst_evh_2248226175642056_command_bt5clc4rm =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_az0t9zsqm,
      event: 'command',
      when: inst_evh_2248226175642056_command_bt5clc4rm_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_pih1np55r,
      inst_evh_2248226175642056_command_bt5clc4rm,
    ],
    event: 'command',
  });

  const actions = {
    inst_evh_7315092382398562_close_61u8jwiuu,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_7eoge1nta,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_aeopa0ra0,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_pih1np55r,
    inst_evh_2248226175642056_command_bt5clc4rm,
    inst_evh_2248226175642056_command,
  };

  const evh_7315092382398562_close_61u8jwiuu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_61u8jwiuu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_7eoge1nta = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_7eoge1nta.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_aeopa0ra0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_aeopa0ra0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_pih1np55r = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_pih1np55r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_bt5clc4rm = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_bt5clc4rm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Jobs"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="jobs"
      :headerTitle="pgettext('Jobs', 'Jobs')"
      :items="ds"
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <PageHelp path="/general/jobs.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
